import { createEnum } from '../helpers'
import { type MaterialDistributionChannel } from '../materials'
import { PROVINCE_CODES } from '../provinces'

export const MARKING_CODE_VALUES = [...PROVINCE_CODES, 'EXPORT', 'UM'] as const

export const MARKING_CODES = createEnum(MARKING_CODE_VALUES)
export type MarkingCode = (typeof MARKING_CODE_VALUES)[number]

/**
 * A marking's `common`-namespace i18n key. `MARKING_CODE_VALUES` is the provinces plus two codes that
 * are not provinces, so `EXPORT` and `UM` have keys of their own and the rest reuse the province keys.
 */
export const MARKING_CODE_LABEL_KEYS: Record<MarkingCode, string> = {
  AB: 'province.alberta',
  BC: 'province.britishColumbia',
  MB: 'province.manitoba',
  NB: 'province.newBrunswick',
  NL: 'province.newfoundland',
  NS: 'province.novaScotia',
  NT: 'province.northwestTerritories',
  NU: 'province.nunavut',
  ON: 'province.ontario',
  PE: 'province.princeEdwardIsland',
  QC: 'province.quebec',
  SK: 'province.saskatchewan',
  YT: 'province.yukon',
  EXPORT: 'export',
  UM: 'unmarked',
}

const MARKING_EN_VALUES = [
  'AB - Alberta',
  'BC/ CB - British Columbia',
  'MB - Manitoba',
  'NB - New Brunswick',
  'NF / TN - New Foundland',
  'NS/NÉ - Nova Scotia',
  'ON - Ontario',
  'QC - Quebec',
  'SK - Saskatchewan',
  'CDF / HTC - Canadian Duty Free',
  'CDFQ/HTCQ - Canadian Duty Free',
  'UM /NC - Unmarked',
] as const

/**
 * A DB `marking_en` label — the join key between marking-bearing rows (`fz_material.marking_en`,
 * `unreleased_materials.marking`) and `fuze_crt.material_markings`.
 */
export type MarkingEn = (typeof MARKING_EN_VALUES)[number]

/**
 * Every DB `marking_en` label (as stored in `fuze_cache.fz_material.marking_en`) mapped to its
 * MarkingCode. The labels are NOT the codes (e.g. `NL` → `"NF / TN - New Foundland"`), so mapping a
 * material's marking to a marking-keyed ratio column requires this table. Both Canadian Duty Free
 * variants resolve to `EXPORT`. Territory codes (`NT`/`NU`/`PE`/`YT`) have no `marking_en` in the data,
 * so they are absent here. Junk labels (`"Not Found"`, `"Unknown"`) are intentionally omitted.
 */
export const MARKING_EN_TO_CODE: Record<MarkingEn, MarkingCode> = {
  'AB - Alberta': 'AB',
  'BC/ CB - British Columbia': 'BC',
  'MB - Manitoba': 'MB',
  'NB - New Brunswick': 'NB',
  'NF / TN - New Foundland': 'NL',
  'NS/NÉ - Nova Scotia': 'NS',
  'ON - Ontario': 'ON',
  'QC - Quebec': 'QC',
  'SK - Saskatchewan': 'SK',
  'CDF / HTC - Canadian Duty Free': 'EXPORT',
  'CDFQ/HTCQ - Canadian Duty Free': 'EXPORT', // Quebec Duty Free
  'UM /NC - Unmarked': 'UM',
}

/** Resolves a DB `marking_en` label to its MarkingCode, or `undefined` when unrecognised. */
export const getMarkingCode = (markingEn: string): MarkingCode | undefined =>
  MARKING_EN_TO_CODE[markingEn as MarkingEn]

/**
 * SAP numeric marking code (as found in the Product Type Variant import `province` column and the
 * Channel Mix `MARKING_NAME_ID`) mapped to its DB `marking_en` label. Sourced from `marking
 * mapping.csv`; the label values are kept identical to the keys of {@link MARKING_EN_TO_CODE}.
 * Used to hydrate `unreleased_materials.marking` from imports that carry the code rather than the label.
 */
export const MARKING_CODE_TO_EN: Record<string, MarkingEn> = {
  '00': 'UM /NC - Unmarked',
  '01': 'BC/ CB - British Columbia',
  '02': 'AB - Alberta',
  '03': 'SK - Saskatchewan',
  '04': 'MB - Manitoba',
  '05': 'ON - Ontario',
  '06': 'QC - Quebec',
  '07': 'NS/NÉ - Nova Scotia',
  '08': 'NB - New Brunswick',
  '09': 'NF / TN - New Foundland',
  '31': 'CDF / HTC - Canadian Duty Free',
  '36': 'CDFQ/HTCQ - Canadian Duty Free',
}

/**
 * A SAP numeric marking code, leading zero included. Stored on `fuze_crt.material_markings.marking_code`,
 * and carried by the Product Type Variant import `province` column / the Channel Mix `MARKING_NAME_ID`.
 */
export type MarkingId = keyof typeof MARKING_CODE_TO_EN

/** Resolves a SAP numeric marking code to its DB `marking_en` label, or `undefined` when unrecognised. */
export const getMarkingEnFromCode = (code: string): MarkingEn | undefined =>
  MARKING_CODE_TO_EN[code as MarkingId]

/**
 * The inverse of {@link MARKING_EN_TO_CODE}: every MarkingCode mapped to the DB `marking_en` labels that
 * resolve to it. A single code can carry several labels (e.g. `EXPORT` → both Canadian Duty Free variants).
 * Codes with no `marking_en` label in the data (territory codes `NT`/`NU`/`PE`/`YT`) are absent, so
 * {@link getMarkingEnsForCode} returns `[]` for them and any filter built from it matches nothing.
 */
export const MARKING_CODE_TO_ENS: Partial<Record<MarkingCode, string[]>> = Object.entries(
  MARKING_EN_TO_CODE,
).reduce<Partial<Record<MarkingCode, string[]>>>((acc, [markingEn, code]) => {
  ;(acc[code] ??= []).push(markingEn)
  return acc
}, {})

/** The `marking_en` labels that resolve to a MarkingCode (`[]` when the code has no label in the data). */
export const getMarkingEnsForCode = (code: MarkingCode): string[] => MARKING_CODE_TO_ENS[code] ?? []

/**
 * The one `marking_en` label written when an import supplies a MarkingCode rather than a label.
 * `EXPORT` has two labels; the non-Quebec Canadian Duty Free one is chosen, as the unreleased-materials
 * sync does. Territory codes (`NT`/`NU`/`PE`/`YT`) have no label in the data and are absent.
 */
export const MARKING_CODE_TO_CANONICAL_EN: Partial<Record<MarkingCode, MarkingEn>> = {
  AB: 'AB - Alberta',
  BC: 'BC/ CB - British Columbia',
  MB: 'MB - Manitoba',
  NB: 'NB - New Brunswick',
  NL: 'NF / TN - New Foundland',
  NS: 'NS/NÉ - Nova Scotia',
  ON: 'ON - Ontario',
  QC: 'QC - Quebec',
  SK: 'SK - Saskatchewan',
  EXPORT: 'CDF / HTC - Canadian Duty Free',
  UM: 'UM /NC - Unmarked',
}

/** Resolves a MarkingCode to the label imports store for it, or `undefined` when the code has none. */
export const getCanonicalMarkingEnForCode = (code: string): MarkingEn | undefined =>
  MARKING_CODE_TO_CANONICAL_EN[code as MarkingCode]

/**
 * The `marking_en` labels (as stored in `fuze_cache.fz_material.marking_en`) eligible for each material
 * distribution channel: the province-coded markings plus `UM` (Unmarked) are `domestic`, the Canadian
 * Duty Free markings are `export`. Materials whose marking resolves to neither (unrecognised or NULL
 * labels) match no channel. Territory codes (`NT`/`NU`/`PE`/`YT`) have no `marking_en` label in the data,
 * so they contribute none.
 */
export const MATERIAL_DISTRIBUTION_CHANNEL_TO_MARKING_ENS: Record<
  MaterialDistributionChannel,
  string[]
> = {
  domestic: [
    'AB - Alberta',
    'BC/ CB - British Columbia',
    'MB - Manitoba',
    'NB - New Brunswick',
    'NF / TN - New Foundland',
    'NS/NÉ - Nova Scotia',
    'ON - Ontario',
    'QC - Quebec',
    'SK - Saskatchewan',
    'UM /NC - Unmarked',
  ],
  export: ['CDF / HTC - Canadian Duty Free', 'CDFQ/HTCQ - Canadian Duty Free'],
}

/** The `marking_en` labels eligible for a distribution channel (`UM`/"Other" markings belong to neither). */
export const getMarkingEnsForDistributionChannel = (
  channel: MaterialDistributionChannel,
): string[] => MATERIAL_DISTRIBUTION_CHANNEL_TO_MARKING_ENS[channel]
